import { Formik } from 'formik';
import React, { useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { ComboBox, FormGroup, InlineNotification, Stack, Toggle } from '@carbon/react';
import Row from 'src/components/Base/Row';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { FormikRadioButtonGroup } from 'src/components/Form/FormikRadioButtonGroup';
import MoneyInput from 'src/components/Form/MoneyInput';
import RestaurantBranchSelector from 'src/components/Form/RestaurantBranchSelector';
import { EmployeeRow } from 'src/features/employees';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useOrgMembers } from 'src/hooks/useOrgMembers';
import { useMemberships } from 'src/hooks/useOrganizations';
import { accountStatusOf } from 'src/utils/accountStatus';
import { EmployeeRoles, EmployeeSalaryUnit } from 'src/utils/employee';

interface EmployeeFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: EmployeeRow | null | undefined;
}

// A hire who will never touch the portal must still be recordable — see spec decision 8
// and the redesign note above EmployeeForm. Accepts '' (the field's untouched default)
// alongside a real, validated address so the "no email" path never trips `.email()`.
const OptionalEmail = z.union([z.literal(''), z.string().trim().max(120).email()]).optional();

// Shared by every branch below (both hire modes, and editing) — one definition so the K-string
// parsing rule can't drift between them.
const SalaryField = z.preprocess((a) => parseFloat(`${a}`), z.number().min(0));

const ContactFields = {
  firstName: z.string().max(50).trim(),
  lastName: z.string().max(50).trim(),
  email: OptionalEmail,
  phoneNumber: z.string().max(14).trim(),
  salary: SalaryField,
};

// Editing never re-links a record to a different member (EmployeeUpdateDto carries no
// authUserId, and the server refuses a changed userId with USER_LINK_IMMUTABLE).
const EditSchema = z.object(ContactFields);

// Editing a LINKED employee: their contact details belong to their account — the server never
// writes the account from here and strips all four from the employment row — so the form shows
// them read-only and shapeEmployeePayload leaves them out. Not validated either: a read-only
// value the owner cannot change must not be able to block the save.
const LinkedEditSchema = z.object({ salary: SalaryField });

// New hire: a name, optionally an email/phone, no account linkage.
const NewHireSchema = z.object({
  mode: z.literal('new'),
  ...ContactFields,
});

// Existing member: linkage only, no contact fields — the server resolves authUserId
// against Users.authUserId and nulls any contact columns on a linked row (Task 12a).
const ExistingMemberSchema = z.object({
  mode: z.literal('existing'),
  authUserId: z.string().min(1),
  salary: SalaryField,
});

// Exported so both branches are unit-testable without mounting the form: an existing-member
// submission with no contact fields must validate, and a new hire with no email must
// validate — the second case is the whole point of the redesign (spec decision 8).
export const CreateSchema = z.discriminatedUnion('mode', [NewHireSchema, ExistingMemberSchema]);

/**
 * Resolves the better-auth organization id the way OrgSettings/Invitations.tsx (and
 * useEmployeeInvitations.ts) do: match the active session's organization against the
 * membership list, falling back to the first membership. useTenantContext() has no
 * organizationId of its own.
 */
function useActiveOrganizationId(): string | null {
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  return active?.id ?? null;
}

/**
 * The "existing member" picker for mode === 'existing'. Sends the member's better-auth id
 * (its `userId` field, despite the name — see useOrgMembers) rather than the domain
 * Users.id: the server resolves that id against Users.authUserId (Task 12a).
 */
function ExistingMemberField(props: { value: string; invalid?: boolean; onChange: (id: string | null) => void }) {
  const { t } = useTranslation();
  const organizationId = useActiveOrganizationId();
  const { data: members, isLoading } = useOrgMembers(organizationId);

  const items = useMemo(
    () => (members ?? []).map((member) => ({ id: member.userId, text: `${member.user.name} (${member.user.email})` })),
    [members]
  );

  return (
    <ComboBox
      id="employeeExistingMember"
      titleText={t('employee.existingMember.label')}
      placeholder={t('employee.existingMember.placeholder')}
      items={items}
      selectedItem={items.find((item) => item.id === props.value) ?? null}
      onChange={(event) => props.onChange(event.selectedItem?.id ?? null)}
      itemToString={(item) => (item ? item.text : '')}
      disabled={isLoading}
      invalid={props.invalid}
    />
  );
}

const EmployeeForm = (props: EmployeeFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();
  // accountStatusOf, not `userId` directly, so the "has a linked account" rule stays in one place.
  const isLinked = !!props.initialValues && accountStatusOf(props.initialValues) === 'active';
  const editSchema = isLinked ? LinkedEditSchema : EditSchema;

  useImperativeHandle(ref, () => {
    return {
      submitForm: formikRef.current?.submitForm,
      setFieldValue: formikRef.current?.setFieldValue,
      resetForm: () =>
        formikRef.current?.resetForm({
          values: {
            id: '',
            restaurantBranchId: '',
            userId: '',
            mode: 'new',
            authUserId: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            salary: '',
            extraBonus: '',
            salaryUnit: 'M',
            role: 'E',
            partTime: false,
            activated: true,
          },
        }),
    };
  });

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={toFormikValidationSchema((props.initialValues ? editSchema : CreateSchema) as z.ZodTypeAny)}
        initialValues={{
          id: props.initialValues?.id,
          restaurantBranchId: props.initialValues?.restaurantBranchId,
          userId: props.initialValues?.userId,
          mode: 'new',
          authUserId: '',
          firstName: props.initialValues?.firstName,
          lastName: props.initialValues?.lastName,
          email: props.initialValues?.email,
          phoneNumber: props.initialValues?.phoneNumber,
          salary: props.initialValues?.salary || 0,
          extraBonus: props.initialValues?.extraBonus || 0,
          salaryUnit: props.initialValues?.salaryUnit || 'M',
          role: props.initialValues?.role || 'E',
          partTime: props.initialValues ? props.initialValues.partTime : true,
          activated: props.initialValues ? props.initialValues.activated : true,
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => {
          const isCreating = !props.initialValues;
          const showContactFields = !isCreating || values.mode === 'new';
          return (
            <FormGroup legendText={''}>
              <Stack gap={5}>
                <RestaurantBranchSelector
                  value={values.restaurantBranchId}
                  onChange={(value) => setFieldValue('restaurantBranchId', value)}
                />
                <FormikRadioButtonGroup
                  name={'role'}
                  label={t('common.role')}
                  value={values.role}
                  onChange={(value) => setFieldValue('role', value)}
                  options={EmployeeRoles}
                />
                {isCreating && (
                  <FormikRadioButtonGroup
                    name={'mode'}
                    label={t('employee.mode.label')}
                    value={values.mode}
                    onChange={(value) => setFieldValue('mode', value)}
                    useTranslation
                    options={[
                      { value: 'new', label: 'employee.mode.new' },
                      { value: 'existing', label: 'employee.mode.existing' },
                    ]}
                  />
                )}
                {isCreating && values.mode === 'existing' && (
                  <ExistingMemberField
                    value={values.authUserId}
                    onChange={(id) => setFieldValue('authUserId', id ?? '')}
                  />
                )}
                {showContactFields && (
                  <>
                    <Row gap={2}>
                      <FormikTextInput name={'firstName'} label={t('common.firstName')} readOnly={isLinked} />
                      <FormikTextInput name={'lastName'} label={t('common.lastName')} readOnly={isLinked} />
                    </Row>
                    <FormikTextInput name={'email'} label={t('common.email')} readOnly={isLinked} />
                    <FormikTextInput name={'phoneNumber'} label={t('common.phoneNumber')} readOnly={isLinked} />
                    {isLinked && <p className="cds--form__helper-text">{t('employee.invite.alreadyActive')}</p>}
                  </>
                )}
                <MoneyInput name={'salary'} label={t('common.salary')} />
                <MoneyInput name={'extraBonus'} label={t('common.extraBonus')} />
                <FormikRadioButtonGroup
                  name={'salaryUnit'}
                  label={t('common.salaryUnit')}
                  value={values.salaryUnit}
                  onChange={(value) => setFieldValue('salaryUnit', value)}
                  options={EmployeeSalaryUnit}
                />
                <Row gap={2}>
                  <Toggle
                    labelText={t('common.activation')}
                    labelA={t('common.off')}
                    labelB={t('common.on')}
                    onToggle={(checked) => setFieldValue('activated', checked)}
                    toggled={values.activated}
                    id="activated-employeeForm"
                  />
                  <Toggle
                    labelText={t('common.partTime')}
                    labelA={t('common.no')}
                    labelB={t('common.yes')}
                    onToggle={(checked) => setFieldValue('partTime', checked)}
                    toggled={values.partTime}
                    id="partTime-employeeForm"
                  />
                </Row>
              </Stack>
            </FormGroup>
          );
        }}
      </Formik>
    </div>
  );
};

export default React.forwardRef(EmployeeForm);
